import React from "react"
import styled from "styled-components"
import { Link, useLocation, Location } from "react-router-dom"
import {
	Flex,
	Text,
	PrimaryMenu,
	NavButton,
	PrimaryMenuContext,
	PrimaryMenuItemContextValue,
	scale,
	HEADER_HEIGHT_L,
	HEADER_HEIGHT_S,
} from "@capioweb/ui-components"
import { PrimaryMenuProps } from "../types"
import { MenuItem } from "../../../@types/CapioWeb/menu"
import { HeaderContext } from "../context/HeaderContext"
import { sendNavigationDataLayerPush } from "../../../utils/piwikPro"

const MegaMenuGrid = styled.div`
	width: 100%;
	display: grid;
	grid-template-columns: minmax(0, 25%) 49% minmax(0, 264px);
	grid-column-gap: ${scale.px(3)};
`

const MegaMenuListGrid = styled.div`
	width: 100%;
	display: grid;
	grid-template-columns: repeat(2, minmax(0, 1fr));
	grid-auto-rows: min-content;
	grid-column-gap: ${scale.px(3)};
	padding-top: ${scale.px(1)};
`

interface MapMenuLevelsProps
	extends Pick<PrimaryMenuItemContextValue, "closeAllDropdowns"> {
	location: Location
}

const mapPrimaryMenu = (menu: MenuItem[], props: MapMenuLevelsProps) => {
	return menu.map((item) =>
		item.children && item.children.length > 0 ? (
			<React.Fragment key={item.id}>
				<PrimaryMenu.ItemDropdown
					component={<PrimaryMenu.Item as="span">{item.name}</PrimaryMenu.Item>}
					dropdownId={String(item.id)}
				/>
				<MegaMenu menu={item} />
			</React.Fragment>
		) : (
			<PrimaryMenu.Item key={item.id}>
				<Link
					to={item?.url ?? "#"}
					onClick={() => {
						props.closeAllDropdowns()
						sendNavigationDataLayerPush("main_menu", item.name)
					}}
				>
					{item.name}
				</Link>
			</PrimaryMenu.Item>
		)
	)
}

interface MegaMenuLevelProps {
	menu: MenuItem
}

const MegaMenu: React.FC<MegaMenuLevelProps> = ({ menu, ...rest }) => {
	const { closeAllDropdowns, activeDropdownIds } =
		React.useContext<PrimaryMenuItemContextValue>(PrimaryMenuContext)
	const { isStickyHeader } = React.useContext(HeaderContext)

	const [activeSubmenu, setActiveSubmenu] = React.useState<MenuItem>(null)

	const location = useLocation()

	React.useEffect(() => {
		// Get active submenu from dropdown ID and current location
		const submenu = menu.children.find(
			(submenu) => location.pathname.indexOf(submenu.url) > -1
		)

		if (submenu) {
			setActiveSubmenu(submenu)
		} else {
			setActiveSubmenu(menu.children?.length > 0 ? menu.children[0] : null)
		}
	}, [activeDropdownIds, location])

	return (
		<PrimaryMenu.Dropdown
			key={menu.id}
			id={String(menu.id)}
			offsetTop={isStickyHeader ? HEADER_HEIGHT_S : HEADER_HEIGHT_L}
		>
			<MegaMenuGrid>
				<Flex flexDirection="column" w={1}>
					<Flex w={1} as="nav">
						<Flex as="ul" flexDirection="column">
							{menu.children.map((submenu) => (
								<Flex as="li" key={submenu.id}>
									<NavButton
										aria-controls={String(submenu.id)}
										aria-expanded={activeSubmenu?.id === submenu.id}
										onClick={() => setActiveSubmenu(submenu)}
										active={activeSubmenu?.id === submenu.id}
									>
										{submenu.name}
									</NavButton>
								</Flex>
							))}
						</Flex>
					</Flex>
				</Flex>
				<MegaMenuListGrid id={activeSubmenu ? String(activeSubmenu.id) : null}>
					{activeSubmenu &&
						activeSubmenu?.children.map((item) => {
							const getActiveState = () => {
								if (
									item.url !== activeSubmenu.url &&
									location.pathname.indexOf(item.url) > -1
								) {
									return true
								} else if (location.pathname === item.url) {
									return true
								} else {
									return false
								}
							}

							return (
								<Text.Link
									to={item?.url ?? "#"}
									display="block"
									onClick={() => {
										closeAllDropdowns()
										sendNavigationDataLayerPush("main_menu", item.name)
									}}
									fontSize={1.75}
									lineHeight={2}
									mb={2}
									pr={4}
									key={item.id}
									active={getActiveState()}
									underline
								>
									{item.name}
								</Text.Link>
							)
						})}
				</MegaMenuListGrid>
				<Flex flexDirection="column" flexBasis="25%" w={1}>
					{/* Aside content here */}
				</Flex>
			</MegaMenuGrid>
		</PrimaryMenu.Dropdown>
	)
}

const DesktopPrimaryMenu: React.FC<Pick<PrimaryMenuProps, "menu">> = ({
	menu,
}) => {
	const location = useLocation()

	return (
		<PrimaryMenuContext.Consumer>
			{({ closeAllDropdowns }) => {
				return (
					<PrimaryMenu alignItems="center" ml={{ _: 3, xl: 4 }} mr={3}>
						<PrimaryMenu.Inner>
							{mapPrimaryMenu(menu, {
								closeAllDropdowns,
								location,
							})}
						</PrimaryMenu.Inner>
					</PrimaryMenu>
				)
			}}
		</PrimaryMenuContext.Consumer>
	)
}

export { DesktopPrimaryMenu }
